<script setup lang="ts">
import { formatMoneyWithSymbol } from '@/lib/money'
import qs from 'qs'
import { competitorsApi } from '@/api/client'
import { ref, Ref, watch } from 'vue'
import { CatalogFilters, Item, Vendor } from '@/types'
import { debounce, find } from 'lodash'
import { useUserStore } from '@/stores/UserStore'
import Battery from './Battery.vue'
import BrandLogo from './BrandLogo.vue'
import Grade from './Grade.vue'
import DropdownFilter from './DropdownFilter.vue'
import FilterPanel from './FilterPanel.vue'
import LoadMoreFooter from './LoadMoreFooter.vue'
import { useI18n } from 'vue-i18n'
import { useFilterState } from '@/composables/useFilterState'
import { usePagination } from '@/composables/usePagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { TrendingUp, HandCoins, Smartphone, Laptop, Watch, Tablet, Gamepad2 } from 'lucide-vue-next'

const { t } = useI18n()

// Reactive
const vendor: Ref<Vendor | null> = ref(null)
const list: Ref<Array<Item>> = ref([])
const itemsCount: Ref<number> = ref(0)
const lastUpdate: Ref<string> = ref('')
const filters: Ref<CatalogFilters | null> = ref(null)
const loading = ref(true)
const loadingMore = ref(false)

const {
    activeBrandsFilters,
    activeModelsFilters,
    activeGradesFilters,
    activeStorageFilters,
    activeSearchFilter,
    filtersCount,
    hasActiveFilters,
    hasData,
    resetFilters: resetFilterState,
} = useFilterState({ items: list })

const { page, nextPage, resetPage } = usePagination()

const resetFilters = () => {
    resetFilterState()
    resetPage()
}

// Const
const user = useUserStore()

// Props
const props = defineProps<{
    id: number
}>()

const loadVendorInformation = (id: number) => {
    const selectedVendor = find(user.profile?.vendors, { id: Number(id) })

    if (selectedVendor === undefined || selectedVendor === null) {
        throw new Error('Something went wrong')
    }

    vendor.value = selectedVendor as Vendor
}

const getQuery = () => {
    const query: {
        models?: string[]
        products?: string[]
        page?: number
        brands?: string[]
        grades?: string[]
        storage?: string[]
        search?: string
    } = {}

    if (activeModelsFilters.value.length) {
        query.models = activeModelsFilters.value
    }

    if (activeBrandsFilters.value.length) {
        query.brands = activeBrandsFilters.value
    }

    if (activeGradesFilters.value.length) {
        query.grades = activeGradesFilters.value
    }

    if (activeStorageFilters.value.length) {
        query.storage = activeStorageFilters.value
    }

    if (activeSearchFilter.value) {
        query.search = activeSearchFilter.value
    }

    query.page = page.value

    return query
}

const loadItems = async (id: number) => {
    try {
        loading.value = true

        const queryString = qs.stringify(getQuery())
        const itemsResponse = await competitorsApi.list(id, queryString)

        list.value = itemsResponse.data.items
        itemsCount.value = itemsResponse.data.items_count
        lastUpdate.value = itemsResponse.data.last_update
        filters.value = itemsResponse.data.filters
    } catch (error) {
        console.error(error)
    } finally {
        loading.value = false
    }
}

const loadMore = async () => {
    await loadMoreItems(props.id)
}

const onModelsFilterChange = async (_event: Event) => {
    resetPage()
    await loadItems(props.id)
}

const onProductsFilterChange = async () => {
    resetPage()
    await loadItems(props.id)
}

const onBrandsFilterChange = async (_event: Event) => {
    activeModelsFilters.value = []
    resetPage()
    await loadItems(props.id)
}

const onGradesFilterChange = async (_event: Event) => {
    resetPage()
    await loadItems(props.id)
}

const onStorageFilterChange = async (_event: Event) => {
    resetPage()
    await loadItems(props.id)
}

const onSearchInputChange = debounce(onProductsFilterChange, 500)

const loadMoreItems = async (id: number) => {
    try {
        loadingMore.value = true
        nextPage()

        const queryString = qs.stringify(getQuery())
        const itemsResponse = await competitorsApi.list(id, queryString)

        list.value = list.value.concat(itemsResponse.data.items)
    } catch (error) {
        console.error(error)
    } finally {
        loadingMore.value = false
    }
}

const onResetFiltersBtnClick = async () => {
    resetFilters()
    await loadItems(props.id)
}

// The filters follow the URL on their own (see useFilterState): a sidebar link
// carries none, a back navigation carries the ones it left with
watch(
    () => props.id,
    async (id) => {
        resetPage()
        loadVendorInformation(Number(id))
        await loadItems(Number(id))
    },
)

loadVendorInformation(props.id)
loadItems(props.id)
</script>

<template>
    <div class="space-y-4">
        <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
                <h1 class="text-3xl font-bold tracking-tight">
                    {{ vendor?.name }} {{ vendor?.country.toUpperCase() }}
                </h1>

                <div class="flex flex-wrap items-center gap-3">
                    <Badge :variant="vendor?.type === 'resell' ? 'resell' : 'tradein'">
                        <TrendingUp v-if="vendor?.type === 'resell'" class="size-3 mr-1" />
                        <HandCoins v-else class="size-3 mr-1" />
                        {{ vendor?.type === 'resell' ? 'Resell' : 'Trade-in' }}
                    </Badge>
                    <Badge variant="outline">
                        <Smartphone v-if="vendor?.category.slug === 'smartphone'" class="size-3 mr-1" />
                        <Laptop v-else-if="vendor?.category.slug === 'pc'" class="size-3 mr-1" />
                        <Watch v-else-if="vendor?.category.slug === 'smartwatch'" class="size-3 mr-1" />
                        <Tablet v-else-if="vendor?.category.slug === 'tablet'" class="size-3 mr-1" />
                        <Gamepad2 v-else-if="vendor?.category.slug === 'console'" class="size-3 mr-1" />
                        {{ vendor?.category.name }}
                    </Badge>
                </div>
            </div>
        </div>
    </div>

    <div class="mt-8">
        <!-- Filters & Stats -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div v-if="hasData">
                <div class="text-sm font-medium">
                    {{ t('common.products') }}: <span class="font-bold">{{ itemsCount }}</span>
                </div>
                <div v-if="lastUpdate" class="text-xs text-muted-foreground">
                    {{ t('common.updatedAt', { updatedAt: lastUpdate }) }}
                </div>
            </div>

            <div class="flex flex-row gap-2 ml-auto">
                <template v-if="hasData">
                    <Input
                        v-model="activeSearchFilter"
                        type="text"
                        class="h-10 w-48"
                        :placeholder="t('common.filters.search')"
                        @update:model-value="onSearchInputChange"
                    />

                    <FilterPanel
                        :filters-count="filtersCount"
                        :show="true"
                        @reset="onResetFiltersBtnClick"
                    >
                        <DropdownFilter
                            v-model:value="activeBrandsFilters"
                            :options="filters?.brands || []"
                            :show="!!filters?.brands.length"
                            :button-label="t('common.filters.byBrand')"
                            options-label="Brands"
                            @update:value="onBrandsFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeModelsFilters"
                            :options="filters?.models || []"
                            :show="!!filters?.models.length"
                            :button-label="t('common.filters.byModels')"
                            :options-label="t('common.models')"
                            @update:value="onModelsFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeGradesFilters"
                            :options="filters?.grades || []"
                            :show="!!filters?.grades.length"
                            :button-label="t('common.filters.byGrade')"
                            :options-label="t('common.grades')"
                            @update:value="onGradesFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeStorageFilters"
                            :options="filters?.storage || []"
                            :show="!!filters?.storage.length"
                            :button-label="t('common.filters.byStorage')"
                            :options-label="t('common.storage')"
                            @update:value="onStorageFilterChange"
                        />
                    </FilterPanel>
                </template>
            </div>
        </div>

        <!-- Table -->
        <div class="competitors-table">
            <!-- Header -->
            <div v-if="!loading && list.length > 0" class="competitors-row border-b py-3 text-xs font-medium text-muted-foreground uppercase">
                <div class="competitors-col-brand"></div>
                <div class="competitors-col-product">{{ t('competitor.table.headings.name') }}</div>
                <div class="competitors-col-grade">{{ t('competitor.table.headings.grade') }}</div>
                <div class="competitors-col-price">{{ t('competitor.table.headings.price') }}</div>
                <div class="competitors-col-reseller">{{ t('competitor.table.headings.reseller') }}</div>
            </div>

            <!-- Skeleton rows -->
            <template v-if="loading">
                <div
                    v-for="n in 8"
                    :key="`skeleton-${n}`"
                    class="competitors-row border-b py-3"
                >
                    <div class="competitors-col-brand">
                        <Skeleton class="size-4 rounded" />
                    </div>
                    <div class="competitors-col-product space-y-2">
                        <Skeleton class="h-4 w-3/4" />
                        <Skeleton class="h-3 w-1/2" />
                    </div>
                    <div class="competitors-col-grade">
                        <Skeleton class="h-6 w-12 rounded-full" />
                    </div>
                    <div class="competitors-col-price">
                        <Skeleton class="h-4 w-16" />
                    </div>
                    <div class="competitors-col-reseller">
                        <Skeleton class="h-3 w-20" />
                    </div>
                </div>
            </template>

            <!-- Empty state: no data at all -->
            <div v-if="!loading && list.length === 0 && !hasActiveFilters" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('competitor.empty') }}
            </div>

            <!-- No results: filters active but nothing matches -->
            <div v-else-if="!loading && list.length === 0" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('common.noResults') }}
            </div>

            <!-- Rows -->
            <div
                v-for="item in list"
                v-show="!loading"
                :key="item.id"
                class="competitors-row border-b py-3 transition-colors hover:bg-muted/50"
            >
                <!-- Brand -->
                <div class="competitors-col-brand">
                    <BrandLogo v-if="item.brand" :brand="item.brand.name" colored />
                </div>

                <!-- Product -->
                <div class="competitors-col-product min-w-0">
                    <div class="flex items-center gap-2">
                        <router-link
                            :to="{
                                name: 'dashboard.competitor.items.detail',
                                params: { id: props.id, itemId: item.id },
                            }"
                            class="font-medium underline hover:text-muted-foreground text-sm 2xl:text-base"
                        >
                            {{ item.name }}
                        </router-link>

                        <TooltipProvider v-if="item.catalog_items?.length">
                            <Tooltip>
                                <TooltipTrigger as-child>
                                    <Badge variant="secondary" class="shrink-0 cursor-default">
                                        <span class="relative flex size-1.5 mr-1">
                                            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-50" />
                                            <span class="relative inline-flex size-1.5 rounded-full bg-current" />
                                        </span>
                                        <span class="2xl:hidden">{{ item.catalog_items.length }}</span>
                                        <span class="hidden 2xl:inline">{{ item.catalog_items.length }} {{ t('catalog.details.competitorGrades.deals') }}</span>
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" align="start">
                                    <div class="space-y-1.5 text-xs">
                                        <router-link
                                            v-for="catalogItem in item.catalog_items"
                                            :key="catalogItem.id"
                                            :to="{ name: 'dashboard.catalog.detail', params: { id: catalogItem.id } }"
                                            class="flex items-center gap-2 hover:underline"
                                        >
                                            <span v-if="catalogItem.ean">EAN: {{ catalogItem.ean }}</span>
                                            <span v-if="catalogItem.ean && catalogItem.external_id">·</span>
                                            <span v-if="catalogItem.external_id">ID: {{ catalogItem.external_id }}</span>
                                        </router-link>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <div class="flex flex-wrap items-center gap-x-3 mt-1 text-xs text-muted-foreground">
                        <span v-if="item.storage">{{ item.storage }}</span>
                        <span v-if="item.storage && (item.color || item.battery_type)">·</span>
                        <span v-if="item.color" class="capitalize">{{ item.color }}</span>
                        <span v-if="item.color && item.battery_type">·</span>
                        <Battery v-if="item.battery_type" :status="item.battery_type" class="text-muted-foreground" />
                    </div>
                </div>

                <!-- Grade -->
                <div class="competitors-col-grade">
                    <Grade :grade="item.grade" class="2xl:hidden" abbreviated />
                    <Grade :grade="item.grade" class="hidden 2xl:inline-flex" />
                </div>

                <!-- Price -->
                <div class="competitors-col-price font-semibold tabular-nums">
                    {{ formatMoneyWithSymbol(item.price, item.currency_symbol) }}
                </div>

                <!-- Reseller -->
                <div class="competitors-col-reseller text-xs text-muted-foreground">
                    {{ item.seller_name }}
                </div>
            </div>

            <!-- Load more skeleton rows -->
            <template v-if="loadingMore">
                <div
                    v-for="n in 4"
                    :key="`skeleton-more-${n}`"
                    class="competitors-row border-b py-3"
                >
                    <div class="competitors-col-brand">
                        <Skeleton class="size-4 rounded" />
                    </div>
                    <div class="competitors-col-product space-y-2">
                        <Skeleton class="h-4 w-3/4" />
                        <Skeleton class="h-3 w-1/2" />
                    </div>
                    <div class="competitors-col-grade">
                        <Skeleton class="h-6 w-12 rounded-full" />
                    </div>
                    <div class="competitors-col-price">
                        <Skeleton class="h-4 w-16" />
                    </div>
                    <div class="competitors-col-reseller">
                        <Skeleton class="h-3 w-20" />
                    </div>
                </div>
            </template>
        </div>

        <LoadMoreFooter v-if="list.length > 0" :current-count="list.length" :total-count="itemsCount" @load-more="loadMore" />
    </div>
</template>

<style scoped>
.competitors-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}

.competitors-col-brand {
    width: 1rem;
    flex-shrink: 0;
}

.competitors-col-product {
    flex: 1 1 0%;
    min-width: 0;
    max-width: 48rem;
}

.competitors-col-grade {
    width: 5rem;
    flex-shrink: 0;
}

.competitors-col-price {
    width: 5.5rem;
    flex-shrink: 0;
}

.competitors-col-reseller {
    width: 6rem;
    flex-shrink: 0;
}

@media (min-width: 1536px) {
    .competitors-row {
        gap: 1.5rem;
    }

    .competitors-col-grade {
        width: 7rem;
    }

    .competitors-col-price {
        width: 6.5rem;
    }

    .competitors-col-reseller {
        width: 8rem;
    }
}
</style>
